/**
 * Test-only auth provider for Playwright e2e runs. Replaces
 * `KeycloakProvider` when `VITE_E2E_MODE === 'true'` at build time.
 *
 * Reads a pre-signed test JWT from `localStorage['relay.e2e.token']`
 * — Playwright's `globalSetup` mints that token and seeds storage
 * before any page navigation. The backend still does full JWT
 * validation against a test JWKS served by the `e2e-idp` compose
 * service, so this is a real authenticated request, not a bypass.
 *
 * This module is only imported when `VITE_E2E_MODE` is set (via
 * dynamic `import.meta.env` guard in `App.tsx`) so Vite tree-shakes
 * it out of production bundles.
 */

import { setTokenProvider } from '@/api/client';
import { type ReactNode } from 'react';

const STORAGE_KEY = 'relay.e2e.token';

// Register at module scope, NOT in an effect: children's first queries
// (feature flags, /me) fire during the initial render, before effects
// run — an effect-time registration loses that race and the flags
// query caches a fail-closed 401 result for its whole staleTime.
setTokenProvider(async () => {
  if (typeof window === 'undefined') return undefined;
  return window.localStorage.getItem(STORAGE_KEY) ?? undefined;
});

export function E2EAuthProvider({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
